/**
 * Workspace interaction controller — the shared drag/tear/snap/split state
 * machine, ported from pik's CanvasCore blocks (the reference implementation):
 *
 * - Floating-window drag: the window element is moved directly in the DOM;
 *   the store is written once on drop, so the tree never re-renders per
 *   pointermove.
 * - Tab grab-and-pull: horizontal moves reorder (across split panes too),
 *   pulling below the strip tears the tab out into a floating window that
 *   keeps dragging.
 * - Drop resolution order: tab strip → re-dock at index; snap zone with a
 *   single open tab → re-dock fullscreen; snap zone → split; otherwise stay
 *   floating at the dropped geometry.
 * - Split divider drag with 10–90% clamps.
 * - Maximize / restore-down with remembered geometry.
 */

import { useCallback, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type { DocumentTabsStore } from '../core/createDocumentWorkspace';
import type {
  DocLayoutNode,
  DocSnapZone,
  WorkspaceClassNames,
} from '../types';
import { firstLeafGroup } from '../layout/layoutTree';
import { resolveSnapZone } from '../layout/windowLayout';
import { emitGeometryChanged } from './protocol';

/** Pull distance below the strip that triggers a tear-out */
const TEAR_OUT_THRESHOLD = 18;
/** Horizontal jitter tolerated before a tab drag starts reordering */
const TAB_DRAG_ACTIVATION = 4;
/** Pointer proximity (px) that counts as hovering a group's tab strip */
const STRIP_HOVER_MARGIN_X = 20;

const firstClass = (value: string | undefined): string =>
  (value ?? '').split(' ')[0];

const findWindowElement = (
  floatRootRef: React.MutableRefObject<HTMLDivElement | null>,
  fileId: string,
  grabElement?: HTMLElement | null,
): HTMLElement | null =>
  (grabElement?.closest('[data-doc-id]') as HTMLElement | null) ??
  floatRootRef.current?.querySelector<HTMLElement>(
    `[data-doc-id="${fileId}"]`,
  ) ??
  null;

/** Default docking group: the layout's first leaf */
const defaultGroupId = (store: DocumentTabsStore): string => {
  const layout = store.getState().layout;
  return layout.type === 'leaf' ? layout.groupId : firstLeafGroup(layout);
};

/** Default floating geometry for a torn-out tab: 60% of the float root,
 * centered under the pointer (title bar at the grab point). */
const tearOutGeometry = (
  areaRect: DOMRect | undefined,
  clientX: number,
  clientY: number,
): {
  position: { x: number; y: number };
  size: { width: number; height: number };
} => {
  const width = areaRect ? areaRect.width * 0.6 : 800;
  const height = areaRect ? areaRect.height * 0.6 : 600;
  const posX = clientX - (areaRect?.left ?? 0) - width / 2;
  const posY = clientY - (areaRect?.top ?? 0) - 18;
  return { position: { x: posX, y: posY }, size: { width, height } };
};

/** Drop index within a strip, from the tab midpoints (excluding the
 * dock-preview ghost itself) */
const dropIndexFromTabs = (
  tabBar: HTMLDivElement,
  classNames: WorkspaceClassNames,
  clientX: number,
): number => {
  const tabElements = Array.from(
    tabBar.querySelectorAll<HTMLElement>(
      `.${firstClass(classNames.tab)}:not(.${firstClass(classNames.tabDockPreview)})`,
    ),
  );
  for (let i = 0; i < tabElements.length; i++) {
    const rect = tabElements[i].getBoundingClientRect();
    if (clientX < rect.left + rect.width / 2) return i;
  }
  return tabElements.length;
};

export interface UseWorkspaceInteractionsParams {
  store: DocumentTabsStore;
  classNames: WorkspaceClassNames;
  /** Element that hosts the floating windows: the windows area in single-leaf
   * mode, the split container otherwise. Also the drag coordinate system. */
  floatRootRef: React.MutableRefObject<HTMLDivElement | null>;
  /** Registered tab strips by group id (drop-target hit testing) */
  groupTabRefs: React.MutableRefObject<Map<string, HTMLDivElement>>;
}

export const useWorkspaceInteractions = ({
  store,
  classNames,
  floatRootRef,
  groupTabRefs,
}: UseWorkspaceInteractionsParams) => {
  const [snapZone, setSnapZone] = useState<DocSnapZone | null>(null);
  const snapZoneRef = useRef<DocSnapZone | null>(null);
  const [draggingTabId, setDraggingTabId] = useState<string | null>(null);
  const [tabDropIndex, setTabDropIndex] = useState<number | null>(null);
  const [dockPreviewTab, setDockPreviewTab] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [dockPreviewGroupId, setDockPreviewGroupId] = useState<string | null>(
    null,
  );
  const [splitDividerDragging, setSplitDividerDragging] = useState(false);

  const floatDragRef = useRef<{
    fileId: string;
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    pos: { x: number; y: number } | null;
    areaRect: DOMRect;
    winEl: HTMLElement | null;
    winW: number;
    winH: number;
    dockHint: boolean;
    dockTargetIndex?: number | null;
    dockTargetGroupId?: string | null;
    snapThreshold: number;
  } | null>(null);

  const tabReorderDragRef = useRef<{
    active: boolean;
    fileId: string;
    groupId: string;
    fromIndex: number;
    startX: number;
    startY: number;
    hasMoved: boolean;
    dropIndex: number | null;
    targetGroupId: string | null;
  } | null>(null);

  const splitDividerDragRef = useRef<{
    node: Extract<DocLayoutNode, { type: 'split' }>;
    startX: number;
    startY: number;
    initialSizes: [number, number];
    direction: 'horizontal' | 'vertical';
    containerRect: DOMRect;
  } | null>(null);

  /** Begins a floating-window drag: the window follows the pointer directly;
   * edge/corner zones show a snap preview and the tab-strip region offers
   * re-docking on drop. */
  const beginWindowDrag = useCallback(
    (
      fileId: string,
      clientX: number,
      clientY: number,
      grabElement?: HTMLElement | null,
      pointerId?: number,
    ) => {
      const areaRect = floatRootRef.current?.getBoundingClientRect();
      if (!areaRect) return;
      // Snap threshold is tokenized: apps retune it via --doc-snap-threshold
      const snapThreshold = (() => {
        const el = floatRootRef.current;
        if (!el) return 32;
        const parsed = parseFloat(
          getComputedStyle(el).getPropertyValue('--doc-snap-threshold'),
        );
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 32;
      })();
      const ws = store.getState().windowStates[fileId];
      const winEl = findWindowElement(floatRootRef, fileId, grabElement);
      const winRect = winEl?.getBoundingClientRect();
      const drag = {
        fileId,
        startX: clientX,
        startY: clientY,
        origX: ws?.position?.x ?? clientX - areaRect.left,
        origY: ws?.position?.y ?? clientY - areaRect.top,
        pos: { x: 0, y: 0 },
        areaRect,
        winEl,
        winW: winRect?.width ?? 0,
        winH: winRect?.height ?? 0,
        dockHint: false,
        snapThreshold,
      };
      drag.pos = { x: drag.origX, y: drag.origY };
      floatDragRef.current = drag;
      setSnapZone(null);
      if (grabElement && pointerId !== undefined) {
        let captured = false;
        try {
          grabElement.setPointerCapture(pointerId);
          captured = true;
        } catch {
          /* source element may be unmounting */
        }
        // If the grab element is already gone from DOM (e.g. tab tear-out), try
        // to capture on the window element found in the DOM instead
        if (!captured) {
          const el = findWindowElement(floatRootRef, drag.fileId);
          if (el) {
            try {
              el.setPointerCapture(pointerId);
            } catch {
              /* ignore */
            }
          }
        }
      }

      const finish = (commit: boolean) => {
        const d = floatDragRef.current;
        const zone = snapZoneRef.current;
        const dock = d?.dockHint ?? false;
        const targetIdx = d?.dockTargetIndex;
        floatDragRef.current = null;
        snapZoneRef.current = null;
        setSnapZone(null);
        setDockPreviewTab(null);
        setDockPreviewGroupId(null);
        setTabDropIndex(null);
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
        window.removeEventListener('pointercancel', handleCancel);
        if (!d || !commit) return;
        if (dock) {
          // Released over a group's tab strip — dock into that group at the
          // computed index
          store.getState().dockTab(
            d.fileId,
            d.dockTargetGroupId ?? defaultGroupId(store),
            typeof targetIdx === 'number' ? targetIdx : undefined,
          );
          return;
        }
        if (zone && store.getState().tabIds.length <= 1) {
          // Single open tab: a snap-zone drop re-docks the tab into the strip
          // (Dock as Tab), where it fills the whole pane — no split with an
          // empty second group is created
          store.getState().dockTab(d.fileId, defaultGroupId(store));
          return;
        }
        if (zone) {
          // Snap zone drop → split the workspace into two panes
          const direction =
            zone === 'left' || zone === 'right' ? 'horizontal' : 'vertical';
          const position = zone === 'left' || zone === 'top' ? 'before' : 'after';
          store.getState().splitWithTab(d.fileId, direction, position);
          return;
        }
        if (d.pos) {
          store.getState().setWindowState(d.fileId, {
            position: d.pos,
            dockZone: undefined,
          });
        }
      };

      const handleMove = (ev: PointerEvent) => {
        const d = floatDragRef.current;
        if (!d) return;
        // The button was released outside the webview: treat it as a drop
        // instead of letting the window stick to the pointer forever
        if (ev.buttons === 0) {
          finish(true);
          return;
        }
        // Re-read the area rect every move: the shared tab strip can appear or
        // disappear mid-drag, which shifts and resizes the float root
        const area =
          floatRootRef.current?.getBoundingClientRect() ?? d.areaRect;
        d.areaRect = area;
        let x = d.origX + (ev.clientX - d.startX);
        let y = d.origY + (ev.clientY - d.startY);
        // Clamp so the title bar always stays reachable inside the area
        if (d.winW > 0)
          x = Math.min(
            Math.max(x, -(d.winW - 120)),
            Math.max(0, area.width - 120),
          );
        if (d.winH > 0)
          y = Math.min(Math.max(y, 0), Math.max(0, area.height - 48));
        d.pos = { x, y };
        if (d.winEl) {
          d.winEl.style.left = `${x}px`;
          d.winEl.style.top = `${y}px`;
        }

        const hover = hoverStripAt(
          groupTabRefs,
          ev.clientX,
          ev.clientY,
          STRIP_HOVER_MARGIN_X,
        );
        const hoveredTabBar = hover?.tabBar ?? null;
        const hoveredGroupId = hover?.groupId ?? null;

        let dock = false;
        let calculatedDropIdx: number | null = null;

        // Check if cursor is over any group's tab strip
        if (hoveredTabBar && hoveredGroupId) {
          dock = true;
          calculatedDropIdx = dropIndexFromTabs(
            hoveredTabBar,
            classNames,
            ev.clientX,
          );
        }

        d.dockHint = dock;
        d.dockTargetIndex = calculatedDropIdx;
        d.dockTargetGroupId = hoveredGroupId;

        if (dock) {
          setSnapZone(null);
          snapZoneRef.current = null;
          setTabDropIndex(calculatedDropIdx);
          setDockPreviewGroupId(hoveredGroupId);
          const tab = store.getState().tabs[d.fileId];
          setDockPreviewTab(tab ? { id: tab.id, name: tab.label } : null);
        } else {
          setTabDropIndex(null);
          setDockPreviewTab(null);
          setDockPreviewGroupId(null);

          const relX = ev.clientX - area.left;
          const relY = ev.clientY - area.top;
          const zone = resolveSnapZone(
            relX,
            relY,
            area.width,
            area.height,
            d.snapThreshold,
          );
          snapZoneRef.current = zone;
          setSnapZone(zone);
        }
      };

      const handleUp = () => finish(true);
      const handleCancel = () => finish(false);

      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
      window.addEventListener('pointercancel', handleCancel);
    },
    [store, classNames, floatRootRef, groupTabRefs],
  );

  /** Tab grab-and-pull: moving horizontally within the strip reorders the
   * tabs with an indicator line; pulling vertically below the strip tears
   * that tab out into a floating window that keeps dragging. Supports
   * dragging across split panes. */
  const handleTabPointerDown = useCallback(
    (fileId: string, groupId: string) =>
      (event: ReactPointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) return;
        const grabElement = event.currentTarget;
        const startX = event.clientX;
        const startY = event.clientY;
        const group = store.getState().groups[groupId];
        const fromIndex = group ? group.tabIds.indexOf(fileId) : -1;

        tabReorderDragRef.current = {
          active: true,
          fileId,
          groupId,
          fromIndex,
          startX,
          startY,
          hasMoved: false,
          dropIndex: null,
          targetGroupId: groupId,
        };

        const handleMove = (ev: PointerEvent) => {
          const state = tabReorderDragRef.current;
          if (!state || !state.active) return;

          if (ev.buttons === 0) {
            cleanup(true);
            return;
          }

          const dx = ev.clientX - state.startX;
          const dy = ev.clientY - state.startY;

          if (!state.hasMoved) {
            if (Math.hypot(dx, dy) < TAB_DRAG_ACTIVATION) return;
            state.hasMoved = true;
            setDraggingTabId(fileId);
            setDockPreviewGroupId(groupId);
            try {
              grabElement.setPointerCapture(ev.pointerId);
            } catch {
              /* ignore */
            }
          }

          // Hovering over any group's strip (supports cross-pane dragging)
          const hover = hoverStripAt(groupTabRefs, ev.clientX, ev.clientY, 20);

          if (hover) {
            const calculatedDropIndex = dropIndexFromTabs(
              hover.tabBar,
              classNames,
              ev.clientX,
            );

            state.dropIndex = calculatedDropIndex;
            state.targetGroupId = hover.groupId;
            setDockPreviewGroupId(hover.groupId);

            // Suppress indicator only if reordering in the same group and
            // position wouldn't change
            if (
              hover.groupId === state.groupId &&
              state.fromIndex !== -1 &&
              (calculatedDropIndex === state.fromIndex ||
                calculatedDropIndex === state.fromIndex + 1)
            ) {
              setTabDropIndex(null);
            } else {
              setTabDropIndex(calculatedDropIndex);
            }
            return;
          }

          // Not over any strip: pulling below the origin strip tears the tab
          // out into a floating window — only when a float root exists (a
          // WorkspaceShell hosts it; shell-less strip consumers keep
          // tear-out disabled and get pure reorder behavior)
          const originTabsContainer =
            groupTabRefs.current.get(state.groupId) ?? null;
          const originTabsRect = originTabsContainer?.getBoundingClientRect();

          if (
            floatRootRef.current &&
            originTabsRect &&
            ev.clientY > originTabsRect.bottom + TEAR_OUT_THRESHOLD
          ) {
            cleanup(false);
            setDraggingTabId(null);
            setTabDropIndex(null);
            setDockPreviewGroupId(null);

            const rect = floatRootRef.current?.getBoundingClientRect();
            const geometry = tearOutGeometry(rect, ev.clientX, ev.clientY);
            const posX = geometry.position.x;
            const posY = geometry.position.y;

            store.getState().floatTab(fileId, geometry);
            store.getState().activateTab(fileId);

            // Two nested rAFs give React enough time to commit the floatTab
            // state change and render the new window element into the DOM
            // before we try to query and capture it.
            requestAnimationFrame(() =>
              requestAnimationFrame(() => {
                const winEl = findWindowElement(floatRootRef, fileId);
                if (winEl) {
                  winEl.style.left = `${posX}px`;
                  winEl.style.top = `${posY}px`;
                }
                beginWindowDrag(
                  fileId,
                  ev.clientX,
                  ev.clientY,
                  winEl ?? grabElement,
                  ev.pointerId,
                );
              }),
            );
            return;
          }

          // Pointer is outside any strip but hasn't exceeded tear-out threshold
          state.dropIndex = null;
          setTabDropIndex(null);
        };

        const cleanup = (commit: boolean) => {
          const state = tabReorderDragRef.current;
          tabReorderDragRef.current = null;
          window.removeEventListener('pointermove', handleMove);
          window.removeEventListener('pointerup', handleUp);
          window.removeEventListener('pointercancel', handleCancel);

          setDraggingTabId(null);
          setTabDropIndex(null);
          setDockPreviewGroupId(null);

          if (commit && state && state.hasMoved && state.dropIndex !== null) {
            if (state.targetGroupId && state.targetGroupId !== state.groupId) {
              // Transfer tab directly across split pane groups
              store.getState().dockTab(state.fileId, state.targetGroupId, state.dropIndex);
              store.getState().activateTab(state.fileId);
            } else if (state.fromIndex !== -1) {
              // Reorder in same group
              if (
                state.dropIndex !== state.fromIndex &&
                state.dropIndex !== state.fromIndex + 1
              ) {
                store.getState().reorderInGroup(
                  state.groupId,
                  state.fromIndex,
                  state.dropIndex,
                );
              }
            }
          }
        };

        const handleUp = () => cleanup(true);
        const handleCancel = () => cleanup(false);

        window.addEventListener('pointermove', handleMove);
        window.addEventListener('pointerup', handleUp);
        window.addEventListener('pointercancel', handleCancel);
      },
    [beginWindowDrag, store, classNames, floatRootRef, groupTabRefs],
  );

  /** Split divider drag: resizes the two child panes of one split node */
  const handleSplitDividerPointerDown = useCallback(
    (node: Extract<DocLayoutNode, { type: 'split' }>) =>
      (event: ReactPointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) return;
        const containerEl = event.currentTarget.parentElement;
        if (!containerEl) return;
        const containerRect = containerEl.getBoundingClientRect();
        splitDividerDragRef.current = {
          node,
          startX: event.clientX,
          startY: event.clientY,
          initialSizes: [...node.sizes] as [number, number],
          direction: node.direction,
          containerRect,
        };
        setSplitDividerDragging(true);
        event.currentTarget.setPointerCapture(event.pointerId);

        const handleMove = (ev: PointerEvent) => {
          const d = splitDividerDragRef.current;
          if (!d) return;
          const totalPx =
            d.direction === 'horizontal'
              ? d.containerRect.width
              : d.containerRect.height;
          const delta =
            d.direction === 'horizontal'
              ? ev.clientX - d.startX
              : ev.clientY - d.startY;
          const deltaPercent = (delta / totalPx) * 100;
          const newSize0 = Math.min(
            90,
            Math.max(10, d.initialSizes[0] + deltaPercent),
          );
          const newSize1 = 100 - newSize0;
          store.getState().setNodeSplitSizes(d.node, [newSize0, newSize1]);
        };

        const handleEnd = () => {
          splitDividerDragRef.current = null;
          setSplitDividerDragging(false);
          window.removeEventListener('pointermove', handleMove);
          window.removeEventListener('pointerup', handleEnd);
          window.removeEventListener('pointercancel', handleEnd);
        };

        window.addEventListener('pointermove', handleMove);
        window.addEventListener('pointerup', handleEnd);
        // A canceled gesture (e.g. a browser takeover) must release the
        // divider exactly like a pointerup, or the drag state sticks.
        window.addEventListener('pointercancel', handleEnd);
      },
    [store],
  );

  /** Maximize / restore toggle for a floating window: the previous geometry is
   * remembered so double-click (or the title-bar button) can restore it */
  const toggleWindowMaximize = useCallback(
    (fileId: string) => {
      const state = store.getState();
      const ws = state.windowStates[fileId];
      const isMax = ws?.size?.width === '100%' && ws?.size?.height === '100%';
      if (isMax) {
        const restore = ws?.restoreState;
        store.getState().setWindowState(
          fileId,
          restore
            ? {
                position: restore.position,
                size: restore.size,
                restoreState: undefined,
                dockZone: undefined,
              }
            : {
                position: { x: 0, y: 0 },
                size: { width: '70%', height: '70%' },
                restoreState: undefined,
                dockZone: undefined,
              },
        );
      } else {
        store.getState().setWindowState(fileId, {
          restoreState: { position: ws?.position, size: ws?.size },
          position: { x: 0, y: 0 },
          size: { width: '100%', height: '100%' },
          dockZone: undefined,
        });
      }
      emitGeometryChanged(fileId);
    },
    [store],
  );

  /** Re-docks a floating window back into the tab strip */
  const dockWindow = useCallback(
    (fileId: string) => {
      store.getState().dockTab(fileId);
    },
    [store],
  );

  /** Header drag entry point (stable identity so memoized windows don't
   * re-render when the shell does) */
  const handleWindowHeaderPointerDown = useCallback(
    (fileId: string, event: ReactPointerEvent<HTMLElement>) => {
      if (event.button !== 0) return;
      beginWindowDrag(
        fileId,
        event.clientX,
        event.clientY,
        event.currentTarget,
        event.pointerId,
      );
    },
    [beginWindowDrag],
  );

  return {
    snapZone,
    draggingTabId,
    tabDropIndex,
    dockPreviewTab,
    dockPreviewGroupId,
    splitDividerDragging,
    beginWindowDrag,
    handleTabPointerDown,
    handleSplitDividerPointerDown,
    toggleWindowMaximize,
    dockWindow,
    handleWindowHeaderPointerDown,
  };
};

export type WorkspaceInteractions = ReturnType<typeof useWorkspaceInteractions>;

interface StripHover {
  tabBar: HTMLDivElement;
  groupId: string;
}

/** Hit-test the registered tab strips; the first strip whose rect (expanded
 * by the given x margin / tight y band) contains the pointer wins. */
const hoverStripAt = (
  groupTabRefs: React.MutableRefObject<Map<string, HTMLDivElement>>,
  clientX: number,
  clientY: number,
  marginX: number,
): StripHover | null => {
  let result: StripHover | null = null;
  groupTabRefs.current.forEach((tabBar, gid) => {
    if (result) return;
    const rect = tabBar.getBoundingClientRect();
    if (
      clientY >= rect.top - 10 &&
      clientY <= rect.bottom + 12 &&
      clientX >= rect.left - marginX &&
      clientX <= rect.right + marginX
    ) {
      result = { tabBar, groupId: gid };
    }
  });
  return result;
};
